/**
 * Hook for building and managing the chat conversation state.
 *
 * Handles:
 * - Building conversation from local/backend timelines
 * - Syncing backend data to chatStateAtom
 * - Determining when to use local vs backend data
 */

import type { ChatState, SessionConversation, TimelineEntry } from '@/components/chat/types/types';
import { useEffect, useMemo } from 'react';
import { buildLegacyTimeline, isConversationStreaming } from '../utils/legacyTimelineBuilder';

const SESSIONLESS_KEY = 'sessionless';

interface ChatMeta {
  updatedAt?: number;
}

interface UseChatConversationParams {
  /** Current chat ID */
  chatId: string | null | undefined;
  /** Session ID (for in-session state migration) */
  sessionId: string | null | undefined;
  /** State key for the current chat */
  stateKey: string;
  /** Current chat state slice */
  currentChatState: SessionConversation | undefined;
  /** Session "new chat" state slice (`session-{id}-new`). Synchronous fallback
   *  during the in-session eager-create key migration so the conversation
   *  timeline isn't briefly empty (which would flash EvidenceChatSkeleton). */
  sessionNewState?: SessionConversation | undefined;
  /** Sessionless state slice */
  sessionlessState: SessionConversation | undefined;
  /** Backend timeline data */
  backendTimeline: TimelineEntry[];
  /** Chat metadata from backend */
  chatMeta: ChatMeta;
  /** Function to update chat state */
  setChatState: (fn: (prev: ChatState) => ChatState) => void;
}

interface UseChatConversationReturn {
  /** The computed conversation object */
  conversation: SessionConversation;
}

/**
 * Hook that builds the conversation from local and backend timelines.
 * Implements the "backend canonical when not streaming" rule.
 */
export function useChatConversation({
  chatId,
  sessionId,
  stateKey,
  currentChatState,
  sessionNewState,
  sessionlessState,
  backendTimeline,
  chatMeta,
  setChatState,
}: UseChatConversationParams): UseChatConversationReturn {
  // DAT-605 FIX: Derive the session-based "new chat" key so we can detect
  // in-session streaming data that was stored under it before chatId arrived.
  const sessionNewKey = sessionId ? `session-${sessionId}-new` : undefined;
  // Build conversation from local/backend timelines
  const conversation: SessionConversation = useMemo(() => {
    // Check for pending sessionless transition first
    if (
      chatId &&
      sessionlessState?.metadata?.clientChatId === chatId &&
      sessionlessState.timeline.length > 0
    ) {
      return sessionlessState;
    }

    // EVI-1680: in-session eager-create bridge. On the first in-session message
    // the chat is created mid-stream, so `stateKey` flips `session-{id}-new` →
    // `chat-{id}` a render *before* the migration effect below moves the data
    // and before the chat refetch lands. Without a synchronous fallback,
    // `conversation.timeline` is briefly empty while `isFetchingChat` is true,
    // so Chat.tsx paints `EvidenceChatSkeleton` for a frame (the reported
    // flash). Surface the session-new timeline directly until the migration
    // completes; it's self-limiting (skipped the moment `currentChatState` has
    // its own timeline).
    if (
      chatId &&
      !currentChatState?.timeline?.length &&
      sessionNewState &&
      sessionNewState.timeline.length > 0 &&
      (sessionNewState.metadata?.clientChatId === chatId ||
        isConversationStreaming(sessionNewState))
    ) {
      return sessionNewState;
    }

    if (currentChatState || backendTimeline.length > 0) {
      const localTimeline = currentChatState?.timeline ?? [];
      const isStreaming = isConversationStreaming(currentChatState);

      const result = buildLegacyTimeline({
        localTimeline,
        backendTimeline,
        isStreaming,
        localLastActivity: currentChatState?.metadata?.lastActivity,
        backendUpdatedAt: chatMeta.updatedAt,
      });

      if (result.source === 'backend' && !currentChatState) {
        return {
          timeline: result.timeline,
          streamingState: null,
          metadata: {
            lastActivity: chatMeta.updatedAt ?? Date.now(),
            ...(chatId && { clientChatId: chatId }),
          },
        };
      }

      if (currentChatState) {
        if (result.source === 'local') {
          if (chatId && !currentChatState.metadata.clientChatId) {
            return {
              ...currentChatState,
              metadata: { ...currentChatState.metadata, clientChatId: chatId },
            };
          }
          return currentChatState;
        }

        return {
          ...currentChatState,
          timeline: result.timeline,
          metadata: {
            ...currentChatState.metadata,
            lastActivity: chatMeta.updatedAt ?? currentChatState.metadata.lastActivity,
            ...(chatId && !currentChatState.metadata.clientChatId && { clientChatId: chatId }),
          },
        };
      }
    }

    return {
      timeline: [],
      streamingState: null,
      metadata: { lastActivity: Date.now(), ...(chatId && { clientChatId: chatId }) },
    };
  }, [
    currentChatState,
    sessionNewState,
    sessionlessState,
    chatId,
    backendTimeline,
    chatMeta.updatedAt,
  ]);

  // DAT-605 FIX: Migrate in-session streaming data when stateKey transitions.
  // When the first in-session message streams, data is stored under
  // "session-${sessionId}-new". When the BE returns chat_id in a metadata chunk,
  // stateKey changes to "chat-${chatId}" — but the streaming data is still under
  // the old key. This effect moves it to the correct key BEFORE the backend sync
  // effect can overwrite it with stale data.
  useEffect(() => {
    if (!chatId || !sessionNewKey || sessionNewKey === stateKey) return;

    setChatState((prev) => {
      // Already have data under the chat key — no migration needed
      if (prev[stateKey]?.timeline?.length) return prev;

      const sessionNewState = prev[sessionNewKey];
      if (
        sessionNewState &&
        sessionNewState.timeline.length > 0 &&
        (sessionNewState.metadata?.clientChatId === chatId ||
         isConversationStreaming(sessionNewState))
      ) {
        const { [sessionNewKey]: _, ...rest } = prev;
        return {
          ...rest,
          [stateKey]: {
            ...sessionNewState,
            metadata: { ...sessionNewState.metadata, lastActivity: Date.now(), clientChatId: chatId },
          },
        };
      }
      return prev;
    });
  }, [chatId, stateKey, sessionNewKey, setChatState]);

  // Sync backend data to chatStateAtom
  useEffect(() => {
    if (!chatId || backendTimeline.length === 0) return;

    setChatState((prev) => {
      const existingState = prev[stateKey];
      const sessionlessStateFromPrev = prev[SESSIONLESS_KEY];

      if (
        sessionlessStateFromPrev?.metadata?.clientChatId === chatId &&
        sessionlessStateFromPrev.timeline.length > 0
      ) {
        return prev;
      }

      if (isConversationStreaming(existingState)) return prev;

      const result = buildLegacyTimeline({
        localTimeline: existingState?.timeline ?? [],
        backendTimeline,
        isStreaming: false,
        localLastActivity: existingState?.metadata?.lastActivity,
        backendUpdatedAt: chatMeta.updatedAt,
      });

      if (result.source === 'local') return prev;

      return {
        ...prev,
        [stateKey]: {
          timeline: result.timeline,
          streamingState: null,
          metadata: { lastActivity: chatMeta.updatedAt ?? Date.now(), clientChatId: chatId },
        },
      };
    });
  }, [chatId, backendTimeline, chatMeta.updatedAt, stateKey, setChatState]);

  return { conversation };
}
